import { Button } from "@opencloud/ui";
import { ChevronRight } from "lucide-react";
import type { AgentBootstrap } from "../bootstrap.js";
import { agentBrand } from "../brand.js";
import type { HostedAgentView } from "../types.js";

/**
 * Agent for a plan that leaves the hosted Agent out. Nothing in Agent can run,
 * so every surface opens here: the paid plan that includes it, next to
 * building from your own AI, which every plan includes. My apps and Sign out
 * are the other ways out, as on the connect screen.
 */
export function HostedAgentUpgrade({ bootstrap, hostedAgent }: {
  bootstrap: AgentBootstrap;
  hostedAgent: HostedAgentView;
}) {
  const brand = agentBrand();
  const { plan } = hostedAgent;
  const agent = `${brand.productName} ${brand.agentName}`;
  const offer = plan
    ? `It's included with ${plan.name}${plan.monthlyPrice ? `, from ${plan.monthlyPrice} a month` : ""}.`
    : "Your plan doesn't include it.";
  const billingUrl = bootstrap.billingUrl ?? new URL("/billing", bootstrap.appsUrl).toString();
  const connectUrl = bootstrap.connectUrl ?? new URL("/connect", bootstrap.appsUrl).toString();
  return (
    <div
      className="a3-theme a3-setup-shell"
      data-oc-theme={brand.themeId}
      data-design-system="opencloud-ui"
    >
      <main className="a3-setup">
        <div className="a3-setup-brand">
          <img
            src={bootstrap.logoUrl}
            alt=""
            aria-hidden="true"
            width="28"
            height="28"
          />
          {agent}
        </div>
        <section
          className="a3-setup-card"
          id="hosted-agent-upgrade"
          aria-labelledby="hosted-agent-upgrade-title"
        >
          <div>
            <p className="a3-eyebrow">{plan ? `${plan.name} plan` : "Paid plans"}</p>
            <h1 id="hosted-agent-upgrade-title">{`Build right here in ${brand.productName}`}</h1>
          </div>
          <p className="a3-setup-lede">
            {`${agent} builds your apps in your browser, tries them privately with made-up data and publishes them when you're ready, using your own AI account. ${offer}`}
          </p>
          <Button
            nativeButton={false}
            render={<a href={billingUrl} />}
            className="a3-action a3-action-primary a3-action-large"
          >
            {plan ? `Upgrade to ${plan.name}` : "See plans"}
          </Button>
          <div className="a3-upgrade-alternative">
            <h2>Or build free from your own AI</h2>
            <p className="a3-setup-quiet">
              {`Connect ${brand.productName} to ChatGPT, Claude, Codex, Claude Code or Cursor, then ask for apps there. Every plan includes it.`}
            </p>
            <Button
              variant="link"
              nativeButton={false}
              render={<a href={connectUrl} />}
              className="a3-upgrade-alternative-link"
            >
              Connect your AI
              <ChevronRight aria-hidden="true" />
            </Button>
          </div>
        </section>
        <nav className="a3-setup-exit" aria-label="Leave Agent">
          <Button
            variant="ghost"
            className="a3-action a3-action-quiet"
            nativeButton={false}
            render={<a href={bootstrap.appsUrl} />}
          >
            My apps
          </Button>
          <form method="post" action={bootstrap.signOutUrl}>
            <Button type="submit" variant="ghost" className="a3-action a3-action-quiet">
              Sign out
            </Button>
          </form>
        </nav>
      </main>
    </div>
  );
}
